import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

/**
 * Liefert den 2-stelligen Locale-Code, den Shopify beim Einbetten der
 * App als `?locale=…`-Query-Parameter mitschickt (z. B. `en-US`, `de`,
 * `nl-BE`). Wir extrahieren das Sprach-Präfix und mappen es auf unsere
 * `supportedLngs`-Liste. Gibt `null` zurück, wenn nichts passendes
 * in der URL steht.
 *
 * Die Order in `detection.order` stellt sicher, dass die Shopify-Admin-
 * Sprache nur dann gewinnt, wenn der User NICHT schon manuell über den
 * Sprach-Umschalter eine Wahl getroffen hat (localStorage hat Vorrang).
 */
const SUPPORTED_LANGS = ["de", "en", "nl", "fr", "it", "pl", "es"] as const;
const SHOPIFY_LOCALE_DETECTOR = {
  name: "shopifyAdminLocale",
  lookup(): string | undefined {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("locale");
      if (!raw) return undefined;
      const prefix = raw.toLowerCase().split("-")[0];
      return (SUPPORTED_LANGS as readonly string[]).includes(prefix) ? prefix : undefined;
    } catch {
      return undefined;
    }
  },
  cacheUserLanguage() {
    // Wir schreiben hier nicht — das macht der localStorage-Detector.
  },
};

// Locale files bundled at build time — no HTTP fetch, no /locales/* dependency.
import deCommon from "./locales/de/common.json";
import deFunnel from "./locales/de/funnel.json";
import deSubscription from "./locales/de/subscription.json";
import enCommon from "./locales/en/common.json";
import enFunnel from "./locales/en/funnel.json";
import enSubscription from "./locales/en/subscription.json";
import nlCommon from "./locales/nl/common.json";
import nlFunnel from "./locales/nl/funnel.json";
import nlSubscription from "./locales/nl/subscription.json";
import frCommon from "./locales/fr/common.json";
import frFunnel from "./locales/fr/funnel.json";
import frSubscription from "./locales/fr/subscription.json";
import itCommon from "./locales/it/common.json";
import itFunnel from "./locales/it/funnel.json";
import itSubscription from "./locales/it/subscription.json";
import plCommon from "./locales/pl/common.json";
import plFunnel from "./locales/pl/funnel.json";
import plSubscription from "./locales/pl/subscription.json";
import esCommon from "./locales/es/common.json";
import esFunnel from "./locales/es/funnel.json";
import esSubscription from "./locales/es/subscription.json";

const detector = new LanguageDetector();
detector.addDetector(SHOPIFY_LOCALE_DETECTOR);

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGS],
    defaultNS: "common",
    ns: ["common", "subscription", "funnel"],
    resources: {
      de: { common: deCommon, funnel: deFunnel, subscription: deSubscription },
      en: { common: enCommon, funnel: enFunnel, subscription: enSubscription },
      nl: { common: nlCommon, funnel: nlFunnel, subscription: nlSubscription },
      fr: { common: frCommon, funnel: frFunnel, subscription: frSubscription },
      it: { common: itCommon, funnel: itFunnel, subscription: itSubscription },
      pl: { common: plCommon, funnel: plFunnel, subscription: plSubscription },
      es: { common: esCommon, funnel: esFunnel, subscription: esSubscription },
    },
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Priorität: (1) manuelle Wahl im localStorage, (2) Shopify-Admin-
      // Sprache aus der URL, (3) Browser-Sprache als letzter Fallback.
      order: ["localStorage", "shopifyAdminLocale", "navigator"],
      caches: ["localStorage"],
    },
  });

export default i18n;
